import styled from '@emotion/styled'
import { FC, useEffect, useMemo } from 'react'
import { MapInner } from 'src/components/map/mapbox-main-map/map-component'
import { MAP_DEFAULT_CENTER_POINT } from 'src/components/map/mapbox/config'
import { Colors } from 'src/components/theme'
import { PARCEL_COLORS_MAP } from 'src/constants/cherre-data-dictionary/zoning-colors'
import { EMPTY_MAP_DATA_SOURCE } from 'src/constants/mapbox'
import { useMap } from 'src/hooks/use-mapbox-map'
import { useMapboxHelper } from 'src/utils/mapbox-main-map'
import turf from 'src/utils/turf'

const PARCEL_SOURCE_ID = 'parcel-map-source'
const PARCEL_LAYER_ID = 'parcel-map-layer'
const PARCEK_LINE_LAYER_ID = 'parcel-map-line-layer'

const MapWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
`

type PolygonProps = {
  geometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon
  mCode?: string
  defaultColor?: string
}

const Polygon: FC<PolygonProps> = ({ geometry, mCode, defaultColor = Colors.greenDark }) => {
  const map = useMap()
  const { useSource, useLayer, setSourceData } = useMapboxHelper(map)
  const feature = useMemo(() => {
    if (!geometry) {
      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [],
        },
      }
    }

    return {
      type: 'Feature',
      geometry,
    }
  }, [geometry])

  const bounds = useMemo(() => (feature.geometry.coordinates.length > 0 ? turf.bbox(feature) : undefined), [feature])

  useSource(PARCEL_SOURCE_ID, {
    type: 'geojson',
    data: EMPTY_MAP_DATA_SOURCE,
  })

  useLayer({
    id: PARCEL_LAYER_ID,
    type: 'fill',
    source: PARCEL_SOURCE_ID,
    paint: {
      'fill-opacity': 0,
    },
  })

  useLayer({
    id: PARCEK_LINE_LAYER_ID,
    type: 'line',
    source: PARCEL_SOURCE_ID,
    paint: {
      'line-width': 0,
    },
  })

  useEffect(() => {
    setSourceData(PARCEL_SOURCE_ID, {
      type: 'FeatureCollection',
      features: [feature],
    })
  }, [feature, setSourceData])

  useEffect(() => {
    if (!mCode) {
      map.setPaintProperty(PARCEL_LAYER_ID, 'fill-color', defaultColor)
      map.setPaintProperty(PARCEK_LINE_LAYER_ID, 'line-color', defaultColor)
    } else {
      const color = PARCEL_COLORS_MAP.get(mCode)
      map.setPaintProperty(PARCEL_LAYER_ID, 'fill-color', color)
      map.setPaintProperty(PARCEK_LINE_LAYER_ID, 'line-color', color)
    }

    map.setPaintProperty(PARCEL_LAYER_ID, 'fill-opacity', 0.3)
    map.setPaintProperty(PARCEK_LINE_LAYER_ID, 'line-width', 1.5)
  }, [mCode, defaultColor])

  useEffect(() => {
    if (!bounds) {
      return
    }

    const [x1, y1, x2, y2] = bounds
    map.fitBounds([x1, y1, x2, y2], {
      padding: 20,
      animate: false,
    })
  }, [bounds])

  return null
}

type ParcelMapProps = {
  preventInteractivity?: boolean
  height?: number
} & PolygonProps

export const ParcelMap: FC<ParcelMapProps> = ({ preventInteractivity = false, geometry, mCode, height = 200 }) => {
  const center = useMemo(() => {
    if (!geometry) {
      return MAP_DEFAULT_CENTER_POINT
    }

    const centroid = turf.centroid(geometry)
    return centroid.geometry.coordinates
  }, [geometry])

  return (
    <MapWrapper style={{ pointerEvents: preventInteractivity ? 'none' : 'initial', height }}>
      <MapInner isStatic zoom={18} bounds={{ center, northEast: [], southWest: [] }} hideZoomControl>
        <Polygon geometry={geometry} mCode={mCode} />
      </MapInner>
    </MapWrapper>
  )
}
